import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { StandardPageHeader } from '@/components/layout/StandardPageHeader';
import { PageContainer } from '@/components/layout/PageContainer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  CreditCard, 
  Pause, 
  Play, 
  X, 
  Search,
  Calendar,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useStripeConnectStatus } from '@/hooks/useStripeConnectStatus';
import { BillingScheduleWithRelations, BillingScheduleStatus } from '@/types/billing';

const BillingSchedules = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = user?.user_metadata?.organization_id;
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSchedules, setSelectedSchedules] = useState<Set<string>>(new Set());
  const [isProcessingBulk, setIsProcessingBulk] = useState(false);
  
  // Check Stripe Connect status
  const { data: stripeStatus, isLoading: stripeLoading } = useStripeConnectStatus();


  const { data: billingSchedules = [], isLoading, error } = useQuery<BillingScheduleWithRelations[]>({
    queryKey: ['billing-schedules', organizationId],
    queryFn: async () => {
      if (!organizationId) {
        return [];
      }
      
      const { data, error } = await supabase
        .from('billing_schedules')
        .select(`
          id,
          enrollment_id,
          organization_id,
          amount_cents,
          billing_day,
          next_billing_date,
          status,
          description,
          created_at,
          enrollment:enrollments!inner(
            id,
            student_id,
            class_id,
            organization_id,
            student:students!enrollments_student_id_fkey(id, first_name, last_name, family_id),
            class:classes!enrollments_class_id_fkey(id, name, price_cents)
          )
        `)
        .eq('enrollment.organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as BillingScheduleWithRelations[];
    },
    enabled: !!organizationId,
  });


  const handleStatusChange = async (scheduleId: string, newStatus: BillingScheduleStatus) => {
    try {
      const { error } = await supabase
        .from('billing_schedules')
        .update({ status: newStatus })
        .eq('id', scheduleId);

      if (error) throw error;

      toast.success(`Billing schedule ${newStatus === 'active' ? 'activated' : newStatus === 'paused' ? 'paused' : 'cancelled'}`);
      queryClient.invalidateQueries({ queryKey: ['billing-schedules'] });
    } catch (error: unknown) {
      toast.error('Failed to update billing schedule');
      console.error(error);
    }
  };

  // Filter schedules based on search and status
  const filteredSchedules = billingSchedules.filter(schedule => {
    const matchesSearch = searchTerm === '' || 
      schedule.enrollment.student.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      schedule.enrollment.student.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      schedule.enrollment.class.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || schedule.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    const variants = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800';
  };

  const totalActiveSchedules = billingSchedules.filter(s => s.status === 'active').length;
  const totalMonthlyRevenue = billingSchedules
    .filter(s => s.status === 'active')
    .reduce((sum, s) => sum + s.amount_cents, 0);

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(filteredSchedules.map(schedule => schedule.id));
      setSelectedSchedules(allIds);
    } else {
      setSelectedSchedules(new Set());
    }
  };

  const handleSelectSchedule = (scheduleId: string, checked: boolean) => {
    const newSelection = new Set(selectedSchedules);
    if (checked) {
      newSelection.add(scheduleId);
    } else {
      newSelection.delete(scheduleId);
    }
    setSelectedSchedules(newSelection);
  };

  // Bulk operations
  const handleBulkStatusChange = async (newStatus: BillingScheduleStatus) => {
    if (selectedSchedules.size === 0) return;
    
    setIsProcessingBulk(true);
    try {
      const promises = Array.from(selectedSchedules).map(scheduleId =>
        supabase
          .from('billing_schedules')
          .update({ status: newStatus })
          .eq('id', scheduleId)
      );
      
      await Promise.all(promises);
      
      toast.success(`${selectedSchedules.size} schedules ${newStatus === 'active' ? 'activated' : newStatus === 'paused' ? 'paused' : 'cancelled'}`);
      queryClient.invalidateQueries({ queryKey: ['billing-schedules'] });
      setSelectedSchedules(new Set());
    } catch (error: unknown) {
      toast.error('Failed to update billing schedules');
      console.error(error);
    } finally {
      setIsProcessingBulk(false);
    }
  };

  const handleExportSelected = () => {
    const selectedData = billingSchedules.filter(schedule => 
      selectedSchedules.has(schedule.id)
    );
    
    // Generate CSV content
    const headers = ['Student', 'Class', 'Amount', 'Billing Day', 'Next Billing', 'Status'];
    const rows = selectedData.map(schedule => [
      `${schedule.enrollment.student.first_name} ${schedule.enrollment.student.last_name}`,
      schedule.enrollment.class.name,
      `$${(schedule.amount_cents / 100).toFixed(2)}`,
      schedule.billing_day,
      format(new Date(schedule.next_billing_date), 'yyyy-MM-dd'),
      schedule.status
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `billing-schedules-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success(`Exported ${selectedData.length} billing schedules`);
  };

  return (
    <div className="space-y-6">
      <StandardPageHeader
        title="Auto-Billing Schedules"
        subtitle="Manage recurring billing schedules for families"
        icon={CreditCard}
      />

      <PageContainer>
        {/* Stripe Connect Status Alert */}
        {!stripeLoading && (
        <div className="mb-6">
          {stripeStatus?.isActive ? (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-green-800">Auto-billing is active</span>
                    <p className="text-sm text-green-700 mt-1">
                      Your Stripe Connect account is connected and ready for automatic billing.
                    </p>
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          ) : stripeStatus?.status === 'not_connected' ? (
            <Alert className="border-yellow-200 bg-yellow-50">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <AlertDescription>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-yellow-800">Stripe Connect not set up</span>
                    <p className="text-sm text-yellow-700 mt-1">
                      Set up Stripe Connect to enable automatic billing for your students.
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open('/admin/settings?tab=payments', '_blank')}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Set up Stripe Connect
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          ) : stripeStatus?.status === 'pending' ? (
            <Alert className="border-blue-200 bg-blue-50">
              <AlertTriangle className="h-4 w-4 text-blue-600" />
              <AlertDescription>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-blue-800">Stripe Connect setup pending</span>
                    <p className="text-sm text-blue-700 mt-1">
                      Complete your Stripe Connect setup to start processing automatic billing.
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open('/admin/settings?tab=payments', '_blank')}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Complete Setup
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          ) : (
            <Alert className="border-red-200 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-red-800">Stripe Connect issue</span>
                    <p className="text-sm text-red-700 mt-1">
                      There's an issue with your Stripe Connect setup. Auto-billing may not work properly.
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open('/admin/settings?tab=payments', '_blank')}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Check Settings
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Schedules</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalActiveSchedules}</div>
            <p className="text-xs text-muted-foreground">
              recurring billing schedules
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${(totalMonthlyRevenue / 100).toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">
              from auto-billing
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Next Billing</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {billingSchedules.filter(s => s.status === 'active').length > 0 
                ? Math.min(...billingSchedules
                    .filter(s => s.status === 'active')
                    .map(s => parseInt(s.next_billing_date.split('-')[2])))
                : '-'}
            </div>
            <p className="text-xs text-muted-foreground">
              earliest billing day
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search by student name or class..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {selectedSchedules.size > 0 && (
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                  {selectedSchedules.size} selected
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedSchedules(new Set())}
                  className="text-blue-600 hover:text-blue-700"
                >
                  Clear selection
                </Button>
              </div>
              
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkStatusChange('paused')}
                  disabled={isProcessingBulk}
                  className="border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                >
                  <Pause className="h-4 w-4 mr-1" />
                  Pause Selected
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkStatusChange('active')}
                  disabled={isProcessingBulk}
                  className="border-green-300 text-green-700 hover:bg-green-50"
                >
                  <Play className="h-4 w-4 mr-1" />
                  Resume Selected
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkStatusChange('cancelled')}
                  disabled={isProcessingBulk}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                >
                  <X className="h-4 w-4 mr-1" />
                  Cancel Selected
                </Button>
                <div className="h-4 w-px bg-gray-300" />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleExportSelected}
                  disabled={isProcessingBulk}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export CSV
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Billing Schedules Table */}
      <Card>
        <CardHeader>
          <CardTitle>Billing Schedules ({filteredSchedules.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : filteredSchedules.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm || statusFilter !== 'all' 
                ? 'No schedules match your filters'
                : 'No billing schedules found. Create enrollments with auto-billing enabled to see them here.'
              }
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <Checkbox
                        checked={filteredSchedules.length > 0 && selectedSchedules.size === filteredSchedules.length}
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all"
                      />
                    </TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Billing Day</TableHead>
                    <TableHead>Next Billing</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSchedules.map((schedule) => (
                    <TableRow key={schedule.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedSchedules.has(schedule.id)}
                          onCheckedChange={(checked) => handleSelectSchedule(schedule.id, checked as boolean)}
                          aria-label={`Select ${schedule.enrollment.student.first_name} ${schedule.enrollment.student.last_name}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {schedule.enrollment.student.first_name} {schedule.enrollment.student.last_name}
                      </TableCell>
                      <TableCell>{schedule.enrollment.class.name}</TableCell>
                      <TableCell className="font-medium">
                        ${(schedule.amount_cents / 100).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {schedule.billing_day}{schedule.billing_day === 1 ? 'st' : schedule.billing_day === 2 ? 'nd' : schedule.billing_day === 3 ? 'rd' : 'th'}
                      </TableCell>
                      <TableCell>
                        {format(new Date(schedule.next_billing_date), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadge(schedule.status)}>
                          {schedule.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {schedule.status === 'active' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(schedule.id, 'paused')}
                            >
                              <Pause className="h-3 w-3" />
                            </Button>
                          )}
                          {schedule.status === 'paused' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(schedule.id, 'active')}
                            >
                              <Play className="h-3 w-3" />
                            </Button>
                          )}
                          {schedule.status !== 'cancelled' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(schedule.id, 'cancelled')}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      </PageContainer>
    </div>
  );
};

export default BillingSchedules;